import { Logger, LogSender } from '../../../logger';
import { StreamingEventBus } from 'streaming/abstract/StreamingEventBus';
import StreamingError, {
  StreamingErrorCodes,
} from '../../abstract/StreamingError';
import {
  DoryMessageType,
  DoryServiceType,
} from '../../abstract/StreamingMessages';
import StreamingSubscription, {
  StreamingSubscriptionEventHandler,
} from '../../abstract/StreamingSubscription';
import {
  getMarketDepthSubscribeRequest,
  MarketDepthRequestPayload,
  MarketDepthSnapshotResponse,
  MarketDepthSubscribeRequest,
  MarketDepthSubscribeResponse,
  MarketDepthUpdateResponse,
} from './Messages';

export type MarketDepthOnSubscribe = (
  msg: MarketDepthSubscribeResponse,
  err: StreamingError
) => void;
export type MarketDepthOnSnapshot = (
  msg: MarketDepthSnapshotResponse,
  err: StreamingError
) => void;
export type MarketDepthOnUpdate = (
  msg: MarketDepthUpdateResponse,
  err: StreamingError
) => void;

export default class MarketDepthSubscription extends StreamingSubscription<
  MarketDepthRequestPayload,
  MarketDepthSubscribeRequest
> {
  private _rics: string[] = [];

  public get subscribed(): MarketDepthRequestPayload {
    return {
      rics: this._rics,
    };
  }
  public get SubscribeRequest(): MarketDepthSubscribeRequest {
    return getMarketDepthSubscribeRequest(this.id, [this.subscribed]);
  }

  public readonly on: {
    [DoryMessageType.SUBSCRIBE]: StreamingSubscriptionEventHandler<MarketDepthSubscribeResponse>;
    [DoryMessageType.SNAPSHOT]: StreamingSubscriptionEventHandler<MarketDepthSnapshotResponse>;
    [DoryMessageType.UPDATE]: StreamingSubscriptionEventHandler<MarketDepthUpdateResponse>;
  } = {
    /**
     * The handler for "subscribe" has to be called when:
     * - a message of with messageType "subscribe" is received and messageId equal to this.id
     *
     * The handler is stopped when:
     * - never
     *
     * Attach an error object if:
     * - payload.subscription.failed !== 0
     */
    [DoryMessageType.SUBSCRIBE]: {
      handler: undefined,
      isAttached: false,
      attach: () => {
        this.on[DoryMessageType.SUBSCRIBE].isAttached = true;
        this.on[DoryMessageType.SUBSCRIBE].detach =
          this.eventBus.on<MarketDepthSubscribeResponse>(
            DoryServiceType.MARKETDEPTH,
            DoryMessageType.SUBSCRIBE,
            (data) => {
              let err: StreamingError = undefined;
              if (data.payload.subscription.failed.length > 0) {
                const failed = data.payload.subscription.failed;
                err = new StreamingError(
                  'Streaming.MarketDepth.MarketDepthSubscription.onSubscribe',
                  StreamingErrorCodes.INVALID_RIC,
                  `Failed to subscribe to ${failed.length} out of ${this._rics.length} rics. Failed: "${failed}".`
                );
              }

              this.on[DoryMessageType.SUBSCRIBE].handler(data, err);
              this.stopTimeout();
            },
            (data) => data.header.messageId === this.id
          );
      },
    },
    /**
     * The handler for "snapshot" has to be called when:
     * - a message of with messageType "snapshot" is received that's not empty and either:
     *   - messageId equal to this.id is received or
     *   - it contains at least all subscribed fids and one of the subscribed rics
     *
     * The handler is stopped when:
     * - never
     *
     * Attach an error object if:
     * - never
     */
    [DoryMessageType.SNAPSHOT]: {
      handler: undefined,
      isAttached: false,
      attach: () => {
        this.on[DoryMessageType.SNAPSHOT].isAttached = true;
        this.on[DoryMessageType.SNAPSHOT].detach =
          this.eventBus.on<MarketDepthSnapshotResponse>(
            DoryServiceType.MARKETDEPTH,
            DoryMessageType.SNAPSHOT,
            (data) => {
              Logger.silly(
                LogSender.STREAMING_MARKETDEPTH,
                `Snapshot received for "${data.payload[0].ric}"`
              );

              this.on[DoryMessageType.SNAPSHOT].handler(data, undefined);
            },
            (data) => data.header.messageId === this.id
          );
      },
    },
    /**
     * The handler for "update" has to be called when:
     * - a message of with messageType "update" is received that contains one of the subscribed rics
     *
     * The handler is stopped when:
     * - the subscription is stopped
     *
     * Attach an error object if:
     * - never
     */
    [DoryMessageType.UPDATE]: {
      handler: undefined,
      isAttached: false,
      attach: () => {
        this.on[DoryMessageType.UPDATE].isAttached = true;
        this.on[DoryMessageType.UPDATE].detach =
          this.eventBus.on<MarketDepthUpdateResponse>(
            DoryServiceType.MARKETDEPTH,
            DoryMessageType.UPDATE,
            (data) => {
              // Check if message ric matches ric, payload[0] is hardcoded because the protocol defines this
              // field as array with a constant length = 1
              if (this._rics.indexOf(data.payload[0].ric) !== -1) {
                this.on[DoryMessageType.UPDATE].handler(data, undefined);
              }
            }
          );
      },
    },
  };

  constructor(
    id: string,
    eventBus: StreamingEventBus,
    rics: string[],
    onSubscribe: MarketDepthOnSubscribe,
    onSnapshot: MarketDepthOnSnapshot,
    onUpdate: MarketDepthOnUpdate
  ) {
    super(id, eventBus);

    this._rics = rics;

    this.on[DoryMessageType.SUBSCRIBE].handler = onSubscribe;
    this.on[DoryMessageType.SNAPSHOT].handler = onSnapshot;
    this.on[DoryMessageType.UPDATE].handler = onUpdate;
  }

  public start(timeout: number): void {
    // Attach all event listeners
    this.on[DoryMessageType.SUBSCRIBE].attach();
    this.on[DoryMessageType.SNAPSHOT].attach();
    this.on[DoryMessageType.UPDATE].attach();
    // Start pending timeout
    this.startTimeout(
      timeout,
      'Streaming.MarketDepth.MarketDepthSubscription.start',
      this.on[DoryMessageType.SUBSCRIBE].handler
    );
  }
  public restart(timeout: number): void {
    // Start pending timeout
    this.startTimeout(
      timeout,
      'Streaming.MarketDepth.MarketDepthSubscription.restart',
      this.on[DoryMessageType.SUBSCRIBE].handler
    );
  }
  public stop(): void {
    // Stop all event listeners
    if (this.on[DoryMessageType.SUBSCRIBE].detach) {
      this.on[DoryMessageType.SUBSCRIBE].detach();
      Logger.silly(
        LogSender.STREAMING_MARKETDEPTH,
        'Detached Subscribe listener'
      );
    }
    if (this.on[DoryMessageType.SNAPSHOT].detach) {
      this.on[DoryMessageType.SNAPSHOT].detach();
      Logger.silly(
        LogSender.STREAMING_MARKETDEPTH,
        'Detached Snapshot listener'
      );
    }
    if (this.on[DoryMessageType.UPDATE].detach) {
      this.on[DoryMessageType.UPDATE].detach();
      Logger.silly(LogSender.STREAMING_MARKETDEPTH, 'Detached Update listener');
    }
    // Stop pending timeout
    this.stopTimeout();
  }
}
